// Catalog (product / restaurant) order statuses.
//
// This file deliberately holds NO status ids. It used to mirror the DB enum by
// hand, and drifted: a PAYMENT_DONE row was added to `product_order_status` and
// every id after CONFIRMED shifted, so the dropdown was writing the status one
// step below the one the supplier picked (COMY1-283).
//
// The names below are the vocabulary; the ids come from the backend at runtime
// via `supplier/jobs/order-statuses`, which reads the lookup table itself.

import { apiGetRequest } from "../Helpers/Requests";
import endpoints from "../Helpers/Endpoints";

export const PRODUCT_STATUS = {
  DRAFT: "DRAFT",
  PENDING: "PENDING",
  WAIT_RE_APPROVE_BY_MEMBER: "WAIT_RE_APPROVE_BY_MEMBER",
  CONFIRMED: "CONFIRMED",
  PREPARING: "PREPARING",
  READY_FOR_PICKUP: "READY_FOR_PICKUP",
  OUT_FOR_DELIVERY: "OUT_FOR_DELIVERY",
  COMPLETED: "COMPLETED",
  CANCELED_BY_MEMBER: "CANCELED_BY_MEMBER",
  CANCELED_BY_SUPPLIER: "CANCELED_BY_SUPPLIER",
};

// Statuses that keep a catalog order inside the supplier's Active filter.
export const ACTIVE_PRODUCT_STATUS_NAMES = [
  PRODUCT_STATUS.CONFIRMED,
  PRODUCT_STATUS.PREPARING,
  PRODUCT_STATUS.READY_FOR_PICKUP,
  PRODUCT_STATUS.OUT_FOR_DELIVERY,
];

// Status name → i18n label key (page-my-jobs › table_data).
export const PRODUCT_STATUS_LABEL_KEY = {
  [PRODUCT_STATUS.CONFIRMED]: "status_confirmed",
  [PRODUCT_STATUS.PREPARING]: "status_preparing",
  [PRODUCT_STATUS.READY_FOR_PICKUP]: "status_ready_for_pickup",
  [PRODUCT_STATUS.OUT_FOR_DELIVERY]: "status_out_for_delivery",
  [PRODUCT_STATUS.COMPLETED]: "status_completed",
};

// The supplier's forward flow, branching on pickup vs delivery:
// Confirmed → Preparing → (Ready for pickup | Out for delivery) → Completed.
export const getNextProductStatusName = (statusName, isPickup) => {
  switch (statusName) {
    case PRODUCT_STATUS.CONFIRMED:
      return PRODUCT_STATUS.PREPARING;
    case PRODUCT_STATUS.PREPARING:
      return isPickup
        ? PRODUCT_STATUS.READY_FOR_PICKUP
        : PRODUCT_STATUS.OUT_FOR_DELIVERY;
    case PRODUCT_STATUS.READY_FOR_PICKUP:
    case PRODUCT_STATUS.OUT_FOR_DELIVERY:
      return PRODUCT_STATUS.COMPLETED;
    default:
      return null;
  }
};

// ── Runtime id ⇄ name map, fetched once per vertical ────────────────────────

/** vertical → Promise<{ idByName, nameById }>. One request per vertical. */
const statusMapCache = new Map();

const EMPTY_MAP = { idByName: {}, nameById: {} };

/**
 * Load the vertical's status lookup from the backend. Cached, so every caller
 * shares one request. On failure the promise is dropped from the cache so a
 * later render can retry rather than being stuck with an empty map.
 */
export const loadProductStatusMap = (itemType = "product") => {
  const key = ["product", "restaurant"].includes(itemType)
    ? itemType
    : "product";
  if (!statusMapCache.has(key)) {
    const request = apiGetRequest(endpoints.getOrderStatuses(key))
      .then((resp) => {
        const rows = resp?.data?.data || [];
        return rows.reduce(
          (acc, row) => {
            acc.idByName[row.status] = row.id;
            acc.nameById[row.id] = row.status;
            return acc;
          },
          { idByName: {}, nameById: {} },
        );
      })
      .catch((err) => {
        console.error("Failed to load product order statuses", err);
        statusMapCache.delete(key);
        return EMPTY_MAP;
      });
    statusMapCache.set(key, request);
  }
  return statusMapCache.get(key);
};
